import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsApi } from '../api/client';
import { LockClosedIcon } from '@heroicons/react/24/outline';

interface FeatureGateProps {
  feature:
    | 'custom_domain'
    | 'contact_forms'
    | 'advanced_components'
    | 'compete_studio'
    | 'meta_capi';
  children: ReactNode;
  fallback?: ReactNode;
  showUpgradePrompt?: boolean;
}

export default function FeatureGate({
  feature,
  children,
  fallback,
  showUpgradePrompt = true,
}: FeatureGateProps) {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkFeatureAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature]);

  const checkFeatureAccess = async () => {
    try {
      setLoading(true);
      const { hasAccess } = await subscriptionsApi.checkFeature(feature);
      setHasAccess(hasAccess);
    } catch (error) {
      console.error('Failed to check feature access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (showUpgradePrompt) {
      return (
        <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 text-center">
          <LockClosedIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Premium Feature</h3>
          <p className="mt-2 text-sm text-gray-600">{getFeatureDescription(feature)}</p>
          <button
            onClick={handleUpgrade}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Upgrade Now
          </button>
        </div>
      );
    }

    return null;
  }

  return <>{children}</>;
}

function getFeatureDescription(feature: string): string {
  switch (feature) {
    case 'custom_domain':
      return 'Custom domains are available on paid plans. Upgrade to connect your own domain.';
    case 'contact_forms':
      return 'Contact forms are available on paid plans. Upgrade to add forms to your websites.';
    case 'advanced_components':
      return 'Advanced components are available on the Professional plan. Upgrade to access premium components.';
    case 'compete_studio':
      return 'Compete Studio is available on the Professional plan. Upgrade to access advanced design tools.';
    case 'meta_capi':
      return 'Meta Conversions API is available on paid plans. Upgrade to enable server-side tracking for better accuracy.';
    default:
      return 'This feature requires a paid plan. Upgrade to unlock it.';
  }
}
